import type { OnboardingUser } from "@fitties/shared";
import { auth } from "@infrastructure/config/firebase";
import { createBackendRegistry } from "@infrastructure/providers/backendRegistry";

import { withOperationTimeout } from "@shared/utils/withOperationTimeout";

const { database } = createBackendRegistry();

const WRITE_TIMEOUT_MS = 15_000;
const WRITE_TIMEOUT_MESSAGE = "Save timed out. Check your connection and try again.";

function isFirestoreNotFound(error: unknown): boolean {
  return (
    typeof error === "object" && error !== null && "code" in error && (error as { code: string }).code === "not-found"
  );
}

/**
 * Onboarding service for managing user profile data during the onboarding process.
 *
 * Provides methods to update and retrieve user profile information using the
 * OnboardingUser schema for type safety and the backend registry pattern.
 *
 * @example
 * ```typescript
 * import onboardingService from '@features/onboarding/services/onboardingService';
 *
 * // Update user profile
 * await onboardingService.updateOnboardingUser('user-123', {
 *   display_name: 'John Doe',
 *   bio: 'Love fitness and hiking'
 * });
 *
 * // Get user data
 * const user = await onboardingService.getOnboardingUser('user-123');
 * ```
 */
const onboardingService = {
  /**
   * Update an onboarding user's profile data.
   *
   * Updates the user's profile with the provided data. Only the fields
   * specified in the updates object will be modified.
   *
   * @param userId - The user's unique identifier
   * @param updates - Partial OnboardingUser data to update
   * @returns Promise that resolves when update is complete
   * @throws {Error} When update fails or user not found
   *
   * @example
   * ```typescript
   * try {
   *   await onboardingService.updateOnboardingUser('user-123', {
   *     display_name: 'John Doe',
   *     bio: 'Love fitness and hiking',
   *     date_of_birth: '1990-01-01'
   *   });
   *   console.log('Profile updated successfully');
   * } catch (error) {
   *   console.error('Failed to update profile:', error);
   * }
   * ```
   */
  async updateOnboardingUser(userId: string, updates: Partial<OnboardingUser>): Promise<void> {
    await auth.authStateReady();

    if (!auth.currentUser?.uid) {
      throw new Error("User not authenticated");
    }

    const write = async () => {
      try {
        await database.updateDocument("users", userId, updates);
      } catch (error) {
        if (isFirestoreNotFound(error)) {
          await database.setDocument("users", userId, { uid: userId, ...updates }, true);
          return;
        }
        throw error;
      }
    };

    await withOperationTimeout(write(), WRITE_TIMEOUT_MS, WRITE_TIMEOUT_MESSAGE);
  },

  /**
   * Get an onboarding user's current profile data.
   *
   * Retrieves the complete user profile data for the specified user ID.
   * Returns null if the user is not found.
   *
   * @param userId - The user's unique identifier
   * @returns Promise resolving to OnboardingUser data or null if not found
   * @throws {Error} When retrieval fails
   *
   * @example
   * ```typescript
   * try {
   *   const user = await onboardingService.getOnboardingUser('user-123');
   *   if (user) {
   *     console.log('User profile:', user.display_name);
   *     console.log('Bio:', user.bio);
   *   } else {
   *     console.log('User not found');
   *   }
   * } catch (error) {
   *   console.error('Failed to get user profile:', error);
   * }
   * ```
   */
  async getOnboardingUser(userId: string): Promise<OnboardingUser | null> {
    return await database.getDocument<OnboardingUser>("users", userId);
  },
};

export default onboardingService;
